import { memo, type FC } from "react";
import { Lock } from "lucide-react";
import { useTranslation } from "react-i18next";
import AudienceListSelect from "src/components/reusable/AudienceListSelect/AudienceListSelect";
import StyledTextInput from "src/components/reusable/StyledTextInput";
import type { AudienceListOption } from "src/utils/audienceListLabels";
import { formatNumber } from "src/utils/numberFormat";
import CampaignFormField from "../../../../shared/CampaignFormField";
import CampaignStepCard from "../../../../shared/CampaignStepCard";
import { usePeopleReachedText } from "../../../../shared/UsePeopleReachedText";
import { getFieldErrorId, useRequiredFieldError } from "../../../../shared/UseRequiredFieldError";
import { MAX_CAMPAIGN_NAME_LENGTH } from "../AiCampaignConstants";

const STEP_KEY = "campaignFlow.aiWizard";
const SETUP_KEY = "campaignFlow.createCampaignNew";
const POPUP_CLASS = "campaign-flow__popup";

const FIELD_IDS = {
  company: "ai-basics-company",
  campaignName: "ai-basics-campaign-name",
  sendTo: "ai-basics-send-to",
  dontSendTo: "ai-basics-dont-send-to",
};

interface CampaignBasicsStepProps {
  title: string;
  campaignName: string;
  onCampaignNameChange: (_value: string) => void;
  companyName: string;
  sendTo: string[];
  excludeList: string[];
  audienceOptions: AudienceListOption[];
  excludeOptions: AudienceListOption[];
  isListsLoading: boolean;
  nameError: string | null;
  notice?: string;
  onSendToChange: (_value: string[]) => void;
  onExcludeListChange: (_value: string[]) => void;
}

const CampaignBasicsStep: FC<CampaignBasicsStepProps> = ({
  title,
  campaignName,
  onCampaignNameChange,
  companyName,
  sendTo,
  excludeList,
  audienceOptions,
  excludeOptions,
  isListsLoading,
  nameError,
  notice,
  onSendToChange,
  onExcludeListChange,
}) => {
  const { t, i18n } = useTranslation();
  const nameField = useRequiredFieldError(campaignName, true);
  const sendToField = useRequiredFieldError(sendTo, true);

  const peopleReachedText = usePeopleReachedText(audienceOptions, sendTo);

  const nameLength = campaignName.length;
  const isAtLimit = nameLength >= MAX_CAMPAIGN_NAME_LENGTH;
  const nameErrorMessage =
    nameError ?? (isAtLimit ? t(`${STEP_KEY}.errors.campaignNameLimit`) : undefined);
  const hasNameError = nameField.hasError || Boolean(nameErrorMessage);

  return (
    <CampaignStepCard title={title} subtitle={t(`${STEP_KEY}.basics.subtitle`)}>
      {notice && <p className="campaign-flow__notice">{notice}</p>}
      <CampaignFormField label={t(`${SETUP_KEY}.company`)} htmlFor={FIELD_IDS.company} required>
        <StyledTextInput
          id={FIELD_IDS.company}
          value={companyName}
          disabled
          suffix={<Lock size={14} aria-hidden />}
          className="campaign-flow__input"
        />
      </CampaignFormField>

      <CampaignFormField
        label={t("campaignFlow.common.campaignName")}
        htmlFor={FIELD_IDS.campaignName}
        required
        hasError={hasNameError}
        errorMessage={nameErrorMessage}
        counter={
          <span
            className={`campaign-flow-field__counter ${
              isAtLimit || nameError ? "campaign-flow-field__counter--error" : ""
            }`}
          >
            {formatNumber(nameLength, {}, i18n.language)}/
            {formatNumber(MAX_CAMPAIGN_NAME_LENGTH, {}, i18n.language)}
          </span>
        }
      >
        <StyledTextInput
          id={FIELD_IDS.campaignName}
          value={campaignName}
          maxLength={MAX_CAMPAIGN_NAME_LENGTH}
          onChange={event => onCampaignNameChange(event.target.value)}
          onBlur={nameField.handleBlur}
          placeholder={t(`${SETUP_KEY}.campaignNamePlaceholder`)}
          autoComplete="off"
          aria-invalid={hasNameError}
          aria-describedby={hasNameError ? getFieldErrorId(FIELD_IDS.campaignName) : undefined}
          className="campaign-flow__input"
        />
      </CampaignFormField>

      <div className="campaign-flow__field-row">
        <CampaignFormField
          label={t(`${SETUP_KEY}.sendTo`)}
          htmlFor={FIELD_IDS.sendTo}
          required
          hasError={sendToField.hasError}
          helperText={peopleReachedText}
        >
          <AudienceListSelect
            id={FIELD_IDS.sendTo}
            value={sendTo}
            options={audienceOptions}
            onChange={onSendToChange}
            onBlur={sendToField.handleBlur}
            aria-invalid={sendToField.hasError}
            aria-describedby={
              sendToField.hasError ? getFieldErrorId(FIELD_IDS.sendTo) : undefined
            }
            loading={isListsLoading}
            placeholder={t(`${SETUP_KEY}.selectAudience`)}
            popupClassName={POPUP_CLASS}
            showSelectedIcon
          />
        </CampaignFormField>

        <CampaignFormField
          label={t(`${SETUP_KEY}.dontSendTo`)}
          htmlFor={FIELD_IDS.dontSendTo}
          hint={t(`${SETUP_KEY}.optionalHint`)}
          helperText={t(`${STEP_KEY}.basics.exclusionHint`)}
        >
          <AudienceListSelect
            id={FIELD_IDS.dontSendTo}
            value={excludeList}
            options={excludeOptions}
            onChange={onExcludeListChange}
            loading={isListsLoading}
            placeholder={t(`${SETUP_KEY}.selectExclusions`)}
            popupClassName={POPUP_CLASS}
            showSelectedIcon
          />
        </CampaignFormField>
      </div>
    </CampaignStepCard>
  );
};

export default memo(CampaignBasicsStep);
